/**
 * Date Utilities
 * 
 * Centralized date formatting functions with proper UTC handling.
 * All backend timestamps are in UTC (ISO 8601 format with 'Z' suffix or without timezone).
 * These functions properly convert to the user's local timezone.
 */

/**
 * Parse a date string, ensuring UTC interpretation for backend timestamps.
 * Handles both ISO strings with 'Z' suffix and those without.
 * 
 * @param {string|number|Date} dateInput - Date string, timestamp, or Date object
 * @returns {Date} - Parsed Date object
 */
export function parseUTCDate(dateInput) {
  if (!dateInput) return null
  
  // Already a Date object
  if (dateInput instanceof Date) return dateInput
  
  // Unix timestamp (seconds) - common from Stripe
  if (typeof dateInput === 'number') {
    // If it's a reasonable timestamp in seconds (before year 2100), multiply by 1000
    if (dateInput < 4102444800) {
      return new Date(dateInput * 1000)
    }
    return new Date(dateInput)
  }
  
  // String - check if it already has timezone info
  const str = String(dateInput)
  
  // If it ends with 'Z' or has +/- timezone offset, it's already properly formatted
  if (/[Zz]$|[+-]\d{2}:\d{2}$/.test(str)) {
    return new Date(str)
  }
  
  // Backend timestamps without 'Z' are in UTC - append 'Z' to ensure UTC interpretation
  // This prevents the browser from interpreting it as local time
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(str)) {
    return new Date(str + 'Z')
  }
  
  // Fallback for other formats
  return new Date(str)
}

/**
 * Format a date for display in the user's locale.
 * Shows date only (no time).
 * 
 * @param {string|number|Date} dateInput - Date to format
 * @param {string} locale - Locale string (default: 'de-DE' for German format)
 * @returns {string} - Formatted date string
 */
export function formatDate(dateInput, locale = 'de-DE') {
  const date = parseUTCDate(dateInput)
  if (!date || isNaN(date.getTime())) return 'N/A'
  
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

/**
 * Format a date with time for display in the user's locale.
 * 
 * @param {string|number|Date} dateInput - Date to format
 * @param {string} locale - Locale string (default: 'de-DE')
 * @param {boolean} showSeconds - Whether to show seconds
 * @returns {string} - Formatted datetime string
 */
export function formatDateTime(dateInput, locale = 'de-DE', showSeconds = false) {
  const date = parseUTCDate(dateInput)
  if (!date || isNaN(date.getTime())) return 'N/A'
  
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }
  
  if (showSeconds) {
    options.second = '2-digit'
  }
  
  return date.toLocaleString(locale, options)
}

/**
 * Format a date with timezone indicator.
 * Useful for showing full context (e.g., "27.11.2025, 14:30 MEZ")
 * 
 * @param {string|number|Date} dateInput - Date to format
 * @param {string} locale - Locale string (default: 'de-DE')
 * @returns {string} - Formatted datetime string with timezone
 */
export function formatDateTimeWithTimezone(dateInput, locale = 'de-DE') {
  const date = parseUTCDate(dateInput)
  if (!date || isNaN(date.getTime())) return 'N/A'
  
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  })
}

/**
 * Format a date in a human-friendly "time ago" format.
 * E.g., "Just now", "5m ago", "2h ago", "3d ago", "27.11.2025"
 * 
 * @param {string|number|Date} dateInput - Date to format
 * @param {string} locale - Locale for fallback date format
 * @returns {string} - Relative time string
 */
export function formatTimeAgo(dateInput, locale = 'de-DE') {
  const date = parseUTCDate(dateInput)
  if (!date || isNaN(date.getTime())) return ''
  
  const now = new Date()
  const diffMs = now - date
  
  // Future dates
  if (diffMs < 0) {
    return formatDate(date, locale)
  }
  
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffSeconds < 60) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  // Older than a week - show full date
  return formatDate(date, locale)
}

/**
 * Format date for short display (used in tables, lists).
 * E.g., "Nov 27" or "27. Nov" depending on locale
 * 
 * @param {string|number|Date} dateInput - Date to format
 * @param {string} locale - Locale string
 * @returns {string} - Short date string
 */
export function formatDateShort(dateInput, locale = 'de-DE') {
  const date = parseUTCDate(dateInput)
  if (!date || isNaN(date.getTime())) return 'N/A'
  
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

/**
 * Format a date range (e.g., subscription period).
 * 
 * @param {string|number|Date} startDate - Start date
 * @param {string|number|Date} endDate - End date
 * @param {string} locale - Locale string
 * @returns {string} - Formatted date range
 */
export function formatDateRange(startDate, endDate, locale = 'de-DE') {
  const start = formatDateShort(startDate, locale)
  const end = formatDateShort(endDate, locale)
  
  if (start === 'N/A' && end === 'N/A') return 'N/A'
  if (start === 'N/A') return `until ${end}`
  if (end === 'N/A') return `from ${start}`
  
  return `${start} - ${end}`
}

/**
 * Compute the whole number of days remaining until a future date, relative
 * to "now" (or an injected reference for tests). Used for demo-trial
 * countdowns ("{n} days left") — never hardcode a trial length; always
 * derive from the actual `current_period_end` timestamp.
 *
 * Rounds UP (ceil) so "23h left" still reads as "1 day left" rather than 0,
 * and clamps at 0 so an already-expired trial doesn't show a negative count.
 *
 * @param {string|number|Date} dateInput - Target end date (e.g. subscription.current_period_end)
 * @param {Date} [now] - Reference "now" (defaults to real current time; override in tests)
 * @returns {number|null} - Whole days remaining (>= 0), or null if dateInput is invalid
 */
export function daysUntil(dateInput, now = new Date()) {
  const date = parseUTCDate(dateInput)
  if (!date || isNaN(date.getTime())) return null

  const diffMs = date.getTime() - now.getTime()
  if (diffMs <= 0) return 0

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
}

// Export for global use (optional - for debugging)
if (typeof window !== 'undefined') {
  window.__BATEMO_DATE_UTILS__ = {
    parseUTCDate,
    formatDate,
    formatDateTime,
    formatDateTimeWithTimezone,
    formatTimeAgo,
    formatDateShort,
    formatDateRange,
    daysUntil
  }
}
